import React, {
  FocusEventHandler,
  ForwardedRef,
  ReactNode,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AlertIcon } from "../../Icons/Icons";
import ErrorText from "../../Text/ErrorText";
import Text from "../../Text/Text";
import InputLabel from "./InputLabel";

type HTMLInputType = HTMLInputElement | HTMLTextAreaElement;

export type InputHandle = {
  focus: (options?: FocusOptions) => void;
  blur: () => void;
  setValue: (value: string) => void;
  getValue: () => string;
  element: HTMLInputType | null;
};

type InputProps = React.InputHTMLAttributes<HTMLInputType> & {
  label?: string;
  errorMessage?: string;
  helpText?: string;
  textArea?: boolean;
  isOnDarkBg?: boolean;
  infoButton?: ReactNode;
};

const Input: React.ForwardRefRenderFunction<InputHandle, InputProps> = (props, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const refPointer = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: (options?: FocusOptions) => {
        if (refPointer.current) {
          refPointer.current.focus(options);
        }
      },
      blur: () => {
        if (refPointer.current) {
          refPointer.current.blur();
        }
      },
      setValue: (value: string) => {
        if (refPointer.current) {
          refPointer.current.value = value;
          const event = new Event("input", { bubbles: true });
          refPointer.current.dispatchEvent(event);
        }
      },
      getValue: () => refPointer.current?.value || "",
      element: refPointer.current,
    }),
    [refPointer.current],
  );

  const handleFocus: FocusEventHandler<HTMLInputType> = (e) => {
    setIsFocused(true);
    props.onFocus && props.onFocus(e);
  };

  const handleBlur: FocusEventHandler<HTMLInputType> = (e) => {
    setIsFocused(false);
    props.onBlur && props.onBlur(e);
  };
  const handleChange: React.ChangeEventHandler<HTMLInputType> = (e) => {
    // setHasValue(e.target.value !== "");
    if (props.inputMode === "numeric") {
      e.target.value = e.target.value.replace(/[^\d]/g, "");
    }
    if (props.inputMode === "decimal" || props.type === "number") {
      e.target.value = e.target.value.replace(/[^\d,.]/g, "");
    }
    if (props.step === 1) {
      e.target.value = e.target.value.replace(/[.,]/g, "");
    }
    props.onChange && props.onChange(e);
  };

  const hasValue =
    !!props.value || !!props.defaultValue || (!!refPointer.current && !!refPointer.current.value);

  const isError = !!props.errorMessage;
  const commonClassnames = `${
    isError
      ? "bg-ldir-red-700 border-ldir-red-900 placeholder-ldir-red-900/60"
      : "bg-white border-ldir-brown-900"
  } flex flex-col text-xl text-ldir-brown-900 focus:border-1.5 focus:outline-none focus:border-ldir-blue-600 w-full p-3 focus:p-2 rounded border-2`;

  return (
    <div className={`relative w-full ${props.className}`}>
      <div className="flex items-end">
        <div className="w-full">
          <InputLabel
            isOnDarkBg={props.isOnDarkBg}
            for={props.id || ""}
            label={props.label || ""}
            isError={isError}
            hasValue={hasValue || !!props.placeholder}
            onClick={() => setIsFocused(true)}
          />
          {props.textArea ? (
            <textarea
              id={props.id}
              ref={refPointer as ForwardedRef<HTMLTextAreaElement>}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={!isFocused ? props.placeholder : ""}
              maxLength={props.maxLength}
              onChange={handleChange}
              aria-required={props.required}
              aria-invalid={isError}
              value={props.value}
              defaultValue={props.defaultValue}
              inputMode={props.inputMode || props.type === "number" ? "numeric" : props.inputMode}
              className={commonClassnames + " min-h-28"}
              aria-describedby={
                isError ? `${props.id}-error` : props.helpText ? `${props.id}-help` : undefined
              }
            />
          ) : (
            <input
              id={props.id}
              ref={refPointer as ForwardedRef<HTMLInputElement>}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={!isFocused ? props.placeholder : ""}
              maxLength={props.maxLength}
              onChange={handleChange}
              value={props.value}
              defaultValue={props.defaultValue}
              inputMode={props.inputMode || props.type === "number" ? "numeric" : props.inputMode}
              className={commonClassnames + " min-h-15"}
              aria-required={props.required}
              type={props.type}
              hidden={props.hidden}
              aria-invalid={isError}
              aria-hidden={props.hidden}
              aria-describedby={
                isError ? `${props.id}-error` : props.helpText ? `${props.id}-help` : undefined
              }
            />
          )}
        </div>
        {props.infoButton && props.infoButton}
      </div>
      {isError && (
        <div
          id={`${props.id}-error`}
          className="mt-1.5 flex items-start wrap-break-word whitespace-normal overflow-hidden"
        >
          <AlertIcon
            className={`mr-1 ${
              isError && props.isOnDarkBg ? " fill-ldir-red-700 text-ldir-red-700" : ""
            }`}
          />
          {props.errorMessage && (
            <ErrorText isOnDarkBg={props.isOnDarkBg}>{props.errorMessage}</ErrorText>
          )}
        </div>
      )}
      {props.helpText && !isError && (
        <div
          id={`${props.id}-help`}
          className={`mt-1 ml-3.5 flex items-start wrap-break-word whitespace-normal overflow-hidden ${
            !isFocused ? "hidden" : ""
          }`}
        >
          <Text className={`${props.isOnDarkBg ? "text-ldir-blue-paler" : "text-ldir-brown-900"}`}>
            {props.helpText}
          </Text>
        </div>
      )}
    </div>
  );
};

export default React.forwardRef<InputHandle, InputProps>(Input);
